import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "./lib/anim";
import { Icon } from "./components/Icons";
import { STUDIO, useStore } from "./lib/store";
import { studioUrl } from "./lib/bridge";
import { useSession } from "./lib/auth";
import { Plate, Avatar } from "./components/ui";

export default function Welcome() {
  const nav = useNavigate();
  const { projects } = useStore();
  const session = useSession();
  const root = useRef<HTMLDivElement>(null);
  const enterStudio = () => nav(session && session.role !== "client" ? "/studio/projects" : "/login?role=studio");
  const enterPortal = () => nav(session?.role === "client" ? `/portal/${session.projectId}` : "/login?role=client");

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power4.out" } });
      tl.from(".w-kick", { y: 20, opacity: 0, duration: 0.7 })
        .from(".w-line", { yPercent: 120, opacity: 0, duration: 1, stagger: 0.12 }, "-=0.4")
        .from(".w-sub", { y: 16, opacity: 0, duration: 0.7 }, "-=0.5")
        .from(".w-card", { y: 30, opacity: 0, duration: 0.7, stagger: 0.12 }, "-=0.4")
        .from(".w-foot", { opacity: 0, duration: 0.6 }, "-=0.3");
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={root} style={{ minHeight: "100vh", background: "var(--paper)", display: "flex", flexDirection: "column", padding: "clamp(24px, 6vw, 72px)", position: "relative", overflow: "hidden" }}>
      {/* ambient plate */}
      <div style={{ position: "absolute", top: "-10%", right: "-8%", width: 520, height: 520, opacity: 0.5, filter: "blur(4px)", pointerEvents: "none" }}>
        <div style={{ borderRadius: "50%", overflow: "hidden", width: "100%", height: "100%" }}><Plate hue={28} h={520} seed={4} /></div>
      </div>

      <header className="row between" style={{ zIndex: 1 }}>
        <div className="row" style={{ gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "var(--ink)", color: "var(--paper)", display: "grid", placeItems: "center" }}><Icon.logo width={22} /></div>
          <div><div className="serif" style={{ fontSize: 20, lineHeight: 1 }}>{STUDIO.name}</div><div className="kicker" style={{ fontSize: 9.5, marginTop: 3 }}>Architecture & Interiors</div></div>
        </div>
        <span className="row" style={{ gap: 8 }}><span className="live-dot" /><span className="dim" style={{ fontSize: 12.5 }}>{projects.length} live projects</span></span>
      </header>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", maxWidth: 1100, zIndex: 1 }}>
        <div className="w-kick kicker" style={{ marginBottom: 18 }}>Studio & Client Cockpit</div>
        <h1 className="serif" style={{ fontSize: "clamp(44px, 8.5vw, 104px)", lineHeight: 0.95, letterSpacing: "-0.03em" }}>
          <div style={{ overflow: "hidden" }}><div className="w-line">Design, shared</div></div>
          <div style={{ overflow: "hidden" }}><div className="w-line" style={{ fontStyle: "italic", color: "var(--accent-ink)" }}>with clarity.</div></div>
        </h1>
        <p className="w-sub muted" style={{ fontSize: "clamp(15px, 2vw, 19px)", maxWidth: 560, marginTop: 26 }}>
          One calm place to shape the brief, present the design, collect approvals, track the programme and keep every client beautifully in the loop.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 18, marginTop: 46, maxWidth: 1080 }}>
          <button className="w-card card hoverable" onClick={enterStudio} style={{ textAlign: "left", padding: 26, cursor: "pointer", background: "var(--ink)", color: "var(--paper)", border: "none" }}>
            <Icon.dashboard width={26} style={{ color: "var(--accent)" }} />
            <div className="serif" style={{ fontSize: 27, marginTop: 20 }}>Studio cockpit</div>
            <p style={{ opacity: 0.7, marginTop: 8, fontSize: 14 }}>Manage projects, presentations, programme, reports & billing.</p>
            <div className="row" style={{ gap: 6, marginTop: 20, fontSize: 13.5, color: "var(--accent)" }}>Enter <Icon.arrow width={16} /></div>
          </button>
          <button className="w-card card hoverable" onClick={enterPortal} style={{ textAlign: "left", padding: 26, cursor: "pointer" }}>
            <Icon.present width={26} style={{ color: "var(--accent-ink)" }} />
            <div className="serif" style={{ fontSize: 27, marginTop: 20 }}>Client portal</div>
            <p className="muted" style={{ marginTop: 8, fontSize: 14 }}>Review designs, approve, comment & see progress — as your client sees it.</p>
            <div className="row" style={{ gap: 6, marginTop: 20, fontSize: 13.5, color: "var(--accent-ink)" }}>Preview <Icon.arrowUpRight width={16} /></div>
          </button>

          {/* cross-link to the Project Studio (Bid Manager / construction site) */}
          <a className="w-card card hoverable" href={studioUrl()} style={{ textAlign: "left", padding: 26, textDecoration: "none", color: "inherit" }}>
            <Icon.layers width={26} style={{ color: "var(--accent-ink)" }} />
            <div className="serif" style={{ fontSize: 27, marginTop: 20 }}>Project Studio</div>
            <p className="muted" style={{ marginTop: 8, fontSize: 14 }}>Take the design to tender & site — Bid Manager, drawings, RFIs, the work programme and daily reports.</p>
            <div className="row" style={{ gap: 6, marginTop: 20, fontSize: 13.5, color: "var(--accent-ink)" }}>Open studio.studiogy.design <Icon.arrowUpRight width={16} /></div>
          </a>
        </div>
      </div>

      <footer className="w-foot row between wrap" style={{ zIndex: 1, gap: 14, marginTop: 30 }}>
        <span className="dim" style={{ fontSize: 12.5, fontStyle: "italic" }}>“{STUDIO.tagline}”</span>
        <span className="dim" style={{ fontSize: 12 }}>{STUDIO.principal}, Principal</span>
      </footer>
    </div>
  );
}
